import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { getConversationDetails, deleteMessage, blockConversation, clearConversationDetails } from "../../store/chat/chat.action";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import { warning } from "../../../util/Alert";

// Render http(s)/www URLs inside a message as clickable links (2026-08-26 —
// links in chats used to render as plain text). Opens in a new tab.
const LINK_RX = /((?:https?:\/\/|www\.)[^\s<>()]+[^\s<>().,;:!?'"\]])/gi;
function linkify(text) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(LINK_RX);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const href = /^https?:\/\//i.test(part) ? part : "https://" + part;
      return (
        <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}>
          {part}
        </a>
      );
    }
    return part;
  });
}


const ChatDetails = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const conversationId = location.state;

  const { conversationDetails, messages, totalMessages } = useSelector((state) => state.chat);

  useEffect(() => {
    if (conversationId) {
      dispatch(getConversationDetails(conversationId));
    }
    return () => {
      dispatch(clearConversationDetails());
    };
  }, [dispatch, conversationId]);

  const handleBack = () => {
    navigate("/admin/chat");
  };

  const handleDeleteMessage = (messageId) => {
    warning("Are you sure you want to delete this message?").then((result) => {
      if (result) {
        props.deleteMessage(messageId, conversationId);
      }
    });
  };

  const handleBlockConversation = () => {
    const isActive = conversationDetails?.isActive;
    warning(`Are you sure you want to ${isActive ? "block" : "unblock"} this conversation?`).then((result) => {
      if (result) {
        props.blockConversation(conversationId);
      }
    });
  };

  if (!conversationDetails) {
    return (
      <div className="mainUserTable">
        <div className="userTable">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const user = conversationDetails.user;
  const seller = conversationDetails.seller;
  const isBlocked = conversationDetails.isBlockedByUser || conversationDetails.isBlockedBySeller || !conversationDetails.isActive;

  return (
    <div className="mainUserTable">
      <div className="userTable">
        <div className="col-12 headname d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link text-white p-0 me-3"
              onClick={handleBack}
            >
              <ArrowBackIcon />
            </button>
            <span>Chat Details</span>
          </div>
          <button
            className={`btn ${isBlocked ? "btn-success" : "btn-danger"} btn-sm`}
            onClick={handleBlockConversation}
          >
            <BlockIcon className="me-1" sx={{ fontSize: 18 }} />
            {isBlocked ? "Unblock Conversation" : "Block Conversation"}
          </button>
        </div>

        <div className="userMain">
          {/* Participants Info */}
          <div className="row mb-4 mt-3">
            <div className="col-md-6">
              <div className="card" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ color: "#333", fontWeight: "600" }}>User</h6>
                  <div className="d-flex align-items-center">
                    <img
                      src={user?.image}
                      alt="User"
                      className="rounded-circle me-3"
                      style={{ width: "60px", height: "60px", objectFit: "cover", border: "2px solid #F0C420" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/dummy.png";
                      }}
                    />
                    <div>
                      <h6 className="mb-0" style={{ color: "#333" }}>{user?.firstName} {user?.lastName}</h6>
                      <small style={{ color: "#666" }}>{user?.email}</small>
                      {user?.uniqueId && (
                        <div>
                          <small style={{ color: "#666" }}>ID: {user?.uniqueId}</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ color: "#333", fontWeight: "600" }}>Seller</h6>
                  <div className="d-flex align-items-center">
                    <img
                      src={seller?.image}
                      alt="Seller"
                      className="rounded-circle me-3"
                      style={{ width: "60px", height: "60px", objectFit: "cover", border: "2px solid #F0C420" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/dummy.png";
                      }}
                    />
                    <div>
                      <h6 className="mb-0" style={{ color: "#333" }}>{seller?.businessName || `${seller?.firstName} ${seller?.lastName}`}</h6>
                      <small style={{ color: "#666" }}>{seller?.email}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card text-center" style={{ backgroundColor: "#F0C420", border: "none" }}>
                <div className="card-body py-3">
                  <h4 className="mb-0" style={{ color: "#333" }}>{totalMessages}</h4>
                  <small style={{ color: "#333" }}>Total Messages</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ backgroundColor: "#3498db", border: "none" }}>
                <div className="card-body py-3">
                  <h4 className="mb-0 text-white">{conversationDetails.userUnreadCount || 0}</h4>
                  <small className="text-white">User Unread</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ backgroundColor: "#6c757d", border: "none" }}>
                <div className="card-body py-3">
                  <h4 className="mb-0 text-white">{conversationDetails.sellerUnreadCount || 0}</h4>
                  <small className="text-white">Seller Unread</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center" style={{ backgroundColor: isBlocked ? "#dc3545" : "#28a745", border: "none" }}>
                <div className="card-body py-3">
                  <h4 className="mb-0 text-white">{isBlocked ? "Blocked" : "Active"}</h4>
                  <small className="text-white">Status</small>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="card" style={{ backgroundColor: "#fff", border: "1px solid #e0e0e0", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
            <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: "#F0C420", borderBottom: "1px solid #e0c41a" }}>
              <h6 className="mb-0" style={{ color: "#333", fontWeight: "600" }}>Messages ({totalMessages})</h6>
            </div>
            <div className="card-body" style={{ maxHeight: "500px", overflowY: "auto", backgroundColor: "#f8f9fa" }}>
              {messages && messages.length > 0 ? (
                messages.map((message, index) => {
                  const isUser = message.senderType === "User";
                  const isDeleted = message.isDeleted;

                  return (
                    <div
                      key={message._id || index}
                      className={`d-flex mb-3 ${isUser ? "justify-content-start" : "justify-content-end"}`}
                    >
                      <div
                        className="p-3 rounded-3 position-relative"
                        style={{
                          maxWidth: "70%",
                          minWidth: "200px",
                          backgroundColor: isDeleted ? "#e0e0e0" : isUser ? "#3498db" : "#F0C420",
                          color: isDeleted ? "#666" : isUser ? "#fff" : "#333",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <small className="fw-bold">
                            {isUser ? "User" : "Seller"}
                          </small>
                          {!isDeleted && (
                            <button
                              className="btn btn-link p-0 ms-2"
                              onClick={() => handleDeleteMessage(message._id)}
                              title="Delete message"
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16, color: isUser ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)" }} />
                            </button>
                          )}
                        </div>
                        <p className="mb-1" style={{ wordBreak: "break-word" }}>
                          {isDeleted ? "[Message deleted]" : linkify(message.content)}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small style={{ opacity: 0.7 }}>
                            {dayjs(message.createdAt).format("DD MMM YYYY HH:mm")}
                          </small>
                          {!isDeleted && (
                            <small style={{ opacity: 0.7 }}>
                              {message.isRead ? "Read" : "Unread"}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-5" style={{ color: "#666" }}>
                  <p>No messages in this conversation</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(null, { getConversationDetails, deleteMessage, blockConversation, clearConversationDetails })(ChatDetails);
